<template>
  <div v-if="shouldDisplayToggle">
    <div id="floor-plan-container" class="d-flex align-center justify-space-between">
      <v-switch
        v-model="shouldShowOriginalFloorPlan"
        class="mt-0 pt-0"
        :class="{ 'primary-label': shouldShowOriginalFloorPlan }"
        :label="$t(`${translationPath}label`)"
        :color="shouldShowOriginalFloorPlan ? 'primary' : 'neutral'"
        hide-details
        inset
      ></v-switch>
      <BetaTag></BetaTag>
      <HelpComponent icon-height="14" :is-open-on-click="false" is-open-on-hover>
        <template #html-content>
          <p class="mb-0">{{ $t(`${translationPath}help`) }}</p>
        </template>
      </HelpComponent>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from "vuex";
import LevelService from "@/services/LevelService";
import HelpComponent from "@/components/shared/HelpComponent.vue";
import BetaTag from "@/components/shared/BetaTag.vue";

export default {
  name: "ShowFloorPlanToggle",
  components: { HelpComponent, BetaTag },
  data: () => ({
    translationPath: "map-settings.floor-plan.",
    shouldShowOriginalFloorPlan: false,
    currentLevelStatuses: null
  }),
  computed: {
    ...mapState("MAP", [
      "shouldShowMapIcons",
      "currentLevel",
      "currentBuilding",
      "currentSite",
      "currentClient",
      "shouldDisplayOriginalFloorPlan"
    ]),
    ...mapGetters("MAP", ["currentLevelObject", "currentBuildingObject"]),
    ...mapState("CONTENT", ["originalMapObjects", "levels", "mapObjects"]),
    ...mapState("BUILDING_EDITOR", ["levelStatuses"]),
    ...mapGetters("MAP", ["map", "currentClientObject"]),
    successfulLevelStatus() {
      return this.currentLevelStatuses?.find(
        (status) => status.levelIndex === this.currentLevel && status.status === "Success"
      );
    },
    fileUrl() {
      return this.successfulLevelStatus?.fileUrl || null;
    },
    fileType() {
      if (!this.fileUrl) {
        return null;
      }
      const url = this.fileUrl?.toLowerCase();
      if (url.includes("dxf")) {
        return "dxf";
      }
      if (url.includes("dwg")) {
        return "dwg";
      }
      if (url.includes("pdf")) {
        return "pdf";
      }
      return null;
    },
    shouldDisplayToggle() {
      return this.shouldShowFloorPlanToggle;
    },
    shouldShowFloorPlanToggle() {
      const FORBIDDEN_ROUTE_NAMES = ["Sites", "BuildingAdd", "BuildingEdit"];
      const ALLOWED_FILE_EXTENSIONS = ["pdf", "dxf", "dwg"];
      if (FORBIDDEN_ROUTE_NAMES.includes(this.$route.name)) {
        return false;
      }
      return ALLOWED_FILE_EXTENSIONS.includes(this.fileType);
    }
  },
  watch: {
    async shouldShowOriginalFloorPlan() {
      if (this.shouldShowOriginalFloorPlan) {
        if (this.originalMapObjects.length === 0) {
          await this.setOriginalFloorPlan();
        }
        this.$store.commit("MAP/SHOULD_DISPLAY_ORIGINAL_FLOOR_PLAN", true);
      } else {
        this.$store.commit("MAP/SHOULD_DISPLAY_ORIGINAL_FLOOR_PLAN", false);
      }
      this.$store.dispatch("MAP/ADD_MAP_OBJECTS_TO_MAP");
    },
    async currentLevel() {
      if (this.currentLevel !== undefined && this.shouldShowOriginalFloorPlan) {
        this.clear();
        await this.setOriginalFloorPlan();
        this.$store.dispatch("MAP/ADD_MAP_OBJECTS_TO_MAP");
      }
    },
    currentBuilding: {
      immediate: true,
      async handler() {
        this.currentLevelStatuses = null;
        if (this.currentClient && this.currentSite && this.currentBuilding) {
          this.currentLevelStatuses = await LevelService.getLevelStatuses(
            this.currentClient,
            this.currentSite,
            this.currentBuilding
          );
        }
        if (this.currentLevel !== undefined && this.shouldShowOriginalFloorPlan) {
          this.clear();
          await this.setOriginalFloorPlan();
          this.$store.dispatch("MAP/ADD_MAP_OBJECTS_TO_MAP");
        }
      }
    },
    currentSite() {
      this.reset();
    },
    shouldDisplayToggle() {
      this.$store.commit("MAP/SHOULD_DISPLAY_ORIGINAL_FLOOR_PLAN_TOGGLE", this.shouldDisplayToggle);
      if (!this.shouldDisplayToggle) {
        this.clear();
      }
    }
  },
  beforeDestroy() {
    this.shouldShowOriginalFloorPlan = false;
    this.reset();
    this.$store.commit("MAP/SHOULD_DISPLAY_ORIGINAL_FLOOR_PLAN_TOGGLE", false);
  },
  methods: {
    blobToBase64(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    },
    async setOriginalFloorPlan() {
      const anchors = this.mapObjects?.filter(
        (feature) =>
          feature?.properties?.typeCode === "georeferencing-anchor" &&
          feature?.properties?.lvl === this.currentLevel &&
          feature?.properties?.bid === this.currentBuilding
      );
      if (anchors.length !== 2 || !this.fileType) {
        return;
      }

      const request = {
        fileType: this.fileType,
        quality: "low",
        georeferencing: {
          anchors: [
            {
              originalCoordinates: anchors?.[0]?.properties?.originalCoordinates,
              coordinates: anchors?.[0]?.geometry?.coordinates
            },
            {
              originalCoordinates: anchors[1].properties.originalCoordinates,
              coordinates: anchors[1].geometry.coordinates
            }
          ]
        },
        inputFileUrl: this.currentLevelObject?.properties?.extra?.fileUrl
      };
      await this.$store.dispatch("CONTENT/SET_ORIGINAL_MAP_OBJECTS", {
        request,
        clientInternalIdentifier: this.$route.params.clientId,
        buildingInternalIdentifier: this.currentBuilding,
        levelIndex: this.currentLevel
      });
    },
    clear() {
      this.$store.commit("CONTENT/ORIGINAL_MAP_OBJECTS", []);
      this.$store.dispatch("MAP/ADD_MAP_OBJECTS_TO_MAP");
    },
    reset() {
      this.shouldShowOriginalFloorPlan = false;
      this.$store.commit("MAP/SHOULD_DISPLAY_ORIGINAL_FLOOR_PLAN", false);
      this.$store.commit("CONTENT/ORIGINAL_MAP_OBJECTS", []);
      this.$store.dispatch("MAP/ADD_MAP_OBJECTS_TO_MAP");
    }
  }
};
</script>
<style lang="scss" scoped>
#floor-plan-container {
  background-color: var(--v-white-base);
  min-width: 168px;
  height: 40px;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 8px;

  ::v-deep {
    .v-label {
      line-height: 12px;
      min-width: 74px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .primary-label {
      .v-label {
        color: var(--v-primary-base) !important;
      }
    }
  }
}
</style>
